<template>
  <div class="main-content-wrapper flex-1 flex relative overflow-hidden">
    <!-- Particle Background -->
    <div class="particle-layer pointer-events-none absolute inset-0">
      <span
        v-for="(star, index) in starParticles"
        :key="`star-${index}`"
        class="particle star hidden dark:block"
        :style="{
          left: `${star.left}%`,
          width: `${star.size}px`,
          height: `${star.size}px`,
          '--rise-duration': `${star.riseDuration}s`,
          '--twinkle-duration': `${star.twinkleDuration}s`,
          '--delay': `${star.delay}s`,
          '--drift': `${star.drift}`,
          '--particle-opacity': `${star.opacity}`,
        }"
      />

      <span
        v-for="(dust, index) in dustParticles"
        :key="`dust-${index}`"
        class="particle dust block dark:hidden"
        :style="{
          left: `${dust.left}%`,
          width: `${dust.size}px`,
          height: `${dust.size}px`,
          '--rise-duration': `${dust.riseDuration}s`,
          '--twinkle-duration': `${dust.twinkleDuration}s`,
          '--delay': `${dust.delay}s`,
          '--drift': `${dust.drift}`,
          '--particle-opacity': `${dust.opacity}`,
        }"
      />
    </div>

    <!-- Full Screen Scene Graph View -->
    <SceneGraphView class="flex-1 min-h-0 relative z-10" />

    <!-- Floating UI Components - placed after SceneGraphView to ensure they render on top -->
    <FloatingSettingsLogo />
    <FloatingLightsBubble />
    
    <!-- Right side stacked bubbles -->
    <div class="fixed right-4 z-50 flex flex-col items-end gap-4" :style="{ top: topOffset }">
      <FloatingAudioBubble />
    </div>
    
    <FloatingDisplaysBar />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import SceneGraphView from '@/views/SceneGraphView.vue'
import FloatingLightsBubble from '@/components/floating/FloatingLightsBubble.vue'
import FloatingAudioBubble from '@/components/floating/FloatingAudioBubble.vue'
import FloatingDisplaysBar from '@/components/floating/FloatingDisplaysBar.vue'
import FloatingSettingsLogo from '@/components/floating/FloatingSettingsLogo.vue'
import { Platform } from '@/lib/platform'

// Add extra top padding on Tauri to account for traffic lights
const topOffset = computed(() => Platform.isDesktop() ? '2.5rem' : '1rem')

type Particle = {
  left: number
  size: number
  delay: number
  riseDuration: number
  twinkleDuration: number
  opacity: number
  drift: number
}

type ParticleConfig = {
  count: number
  size: [number, number]
  rise: [number, number]
  twinkle: [number, number]
  opacity: [number, number]
  drift: [number, number]
}

const starParticles = ref<Particle[]>([])
const dustParticles = ref<Particle[]>([])

const randomWithin = ([min, max]: [number, number]) => min + Math.random() * (max - min)

const createParticles = (config: ParticleConfig): Particle[] =>
  Array.from({ length: config.count }, () => ({
    left: Math.random() * 100,
    size: randomWithin(config.size),
    delay: randomWithin([0, 8]),
    riseDuration: randomWithin(config.rise),
    twinkleDuration: randomWithin(config.twinkle),
    opacity: randomWithin(config.opacity),
    drift: randomWithin(config.drift),
  }))

onMounted(() => {
  starParticles.value = createParticles({
    count: 48,
    size: [1, 2.6],
    rise: [14, 24],
    twinkle: [3.5, 6],
    opacity: [0.35, 0.85],
    drift: [-60, 60],
  })

  dustParticles.value = createParticles({
    count: 36,
    size: [1.4, 4.2],
    rise: [18, 26],
    twinkle: [4, 7],
    opacity: [0.25, 0.55],
    drift: [-30, 30],
  })
})
</script>

<style>
.main-content-wrapper {
  background: var(--bg-gradient);
  transition: background 300ms ease;
}
</style>

<style scoped>

.particle-layer {
  mask-image: radial-gradient(circle at 50% 20%, rgba(255, 255, 255, 0.9) 0%, transparent 70%);
  z-index: 0;
  isolation: isolate;
}

.particle {
  position: absolute;
  bottom: -10vh;
  border-radius: 9999px;
  opacity: 0;
  animation-name: rise, twinkle;
  animation-timing-function: linear, ease-in-out;
  animation-iteration-count: infinite, infinite;
  animation-duration: var(--rise-duration), var(--twinkle-duration);
  animation-delay: var(--delay), calc(var(--delay) * 0.6);
  transform: translate3d(0, 0, 0);
  will-change: transform, opacity;
  contain: layout style;
}

.star {
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.8);
}

.dust {
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 0 4px rgba(255, 255, 255, 0.9);
  filter: blur(0.5px);
}

@keyframes rise {
  0% {
    transform: translate3d(0, 30vh, 0);
    opacity: 0;
  }
  12% {
    opacity: var(--particle-opacity, 0.7);
  }
  70% {
    opacity: var(--particle-opacity, 0.7);
  }
  100% {
    transform: translate3d(calc(var(--drift) * 1px), -110vh, 0);
    opacity: 0;
  }
}

@keyframes twinkle {
  0%,
  100% {
    filter: drop-shadow(0 0 2px rgba(255, 232, 178, 0.3));
  }
  50% {
    filter: drop-shadow(0 0 6px rgba(255, 232, 178, 0.7));
  }
}
</style>